import React from "react";
import { Mail } from "lucide-react";
import SeoHead from "@/components/brand/SeoHead";
import GlowingGridBackground from "@/components/brand/GlowingGridBackground";
import GlassCard from "@/components/brand/GlassCard";
import FadeUp from "@/components/brand/FadeUp";
import {
    FormField,
    FormSelect,
    FormTextarea,
    HoneypotField,
    FormSubmitButton,
    FormErrorBanner,
    FormSuccessState,
} from "@/components/forms/FormControls";
import {
    useFormSubmit,
    isNonEmpty,
    isEmail,
    isPhone,
    minLength,
} from "@/components/forms/useFormSubmit";

const REASONS = [
    "Sponsorship",
    "Student interest",
    "Parent question",
    "Partnership",
    "Media/community",
    "Other",
];

const RESPOND_LIST = [
    "Sponsorship inquiries",
    "Student or parent questions",
    "Partnership conversations",
    "Media or community press",
];

const initialValues = {
    name: "",
    email: "",
    phone: "",
    reasonForContact: "",
    message: "",
    hp_company_website: "",
};

const validate = (v) => {
    const e = {};
    if (!isNonEmpty(v.name)) e.name = "This field is required.";
    if (!isEmail(v.email)) e.email = "Please enter a valid email address.";
    if (v.phone && !isPhone(v.phone))
        e.phone =
            "Please enter a valid phone number (digits, spaces, +, -, ()).";
    if (!isNonEmpty(v.reasonForContact))
        e.reasonForContact = "Please choose a reason.";
    if (!minLength(v.message, 10))
        e.message = "Please write at least 10 characters.";
    return e;
};

export default function Contact() {
    const form = useFormSubmit({
        formType: "contact",
        initialValues,
        validate,
    });

    return (
        <>
            <SeoHead
                title="Contact Vibe for Success"
                description="Contact Vibe for Success, Inc. — questions about Future Builders Lab, sponsorships, parent information, partnerships, or media."
                path="/contact"
            />

            {/* Hero */}
            <section
                className="vfs-section relative overflow-hidden"
                style={{ paddingTop: "clamp(2rem, 6vw, 4rem)" }}
                data-testid="contact-hero"
            >
                <GlowingGridBackground accent="#8B5CF6" opacity={0.16} />
                <div className="vfs-container relative">
                    <div className="max-w-3xl">
                        <FadeUp>
                            <span className="vfs-eyebrow">Contact</span>
                            <h1 className="vfs-h1 mt-5" data-testid="contact-h1">
                                <span className="vfs-grad-text">
                                    Get in touch.
                                </span>
                            </h1>
                            <p
                                className="mt-6 text-lg leading-relaxed"
                                style={{ color: "var(--text-secondary)" }}
                            >
                                We&apos;d love to hear from sponsors, parents,
                                students, partners, and community members.
                            </p>
                        </FadeUp>
                    </div>
                </div>
            </section>

            {/* Two-column */}
            <section
                className="vfs-section"
                data-testid="contact-grid"
                style={{ paddingTop: 0 }}
            >
                <div className="vfs-container">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
                        {/* Info card */}
                        <div className="lg:col-span-5">
                            <FadeUp>
                                <GlassCard
                                    className="p-7 sm:p-9 h-full"
                                    hoverLift={false}
                                >
                                    <p
                                        className="text-xs font-semibold uppercase tracking-wider mb-3"
                                        style={{
                                            color: "var(--text-secondary)",
                                        }}
                                    >
                                        Primary contact
                                    </p>
                                    <a
                                        href="mailto:hello@vibeforsuccess.org"
                                        data-testid="contact-email-link"
                                        className="inline-flex items-center gap-3 vfs-glass px-4 py-3 transition-all hover:bg-white/10"
                                        style={{ borderRadius: 12 }}
                                    >
                                        <Mail
                                            size={18}
                                            color="#C4B5FD"
                                            aria-hidden="true"
                                        />
                                        <span
                                            className="text-sm font-medium"
                                            style={{
                                                color: "var(--text-primary)",
                                            }}
                                        >
                                            hello@vibeforsuccess.org
                                        </span>
                                    </a>
                                    <p
                                        className="text-sm mt-4 leading-relaxed"
                                        style={{
                                            color: "var(--text-secondary)",
                                        }}
                                    >
                                        The contact form on this page is the
                                        fastest way to reach us. Email is
                                        available as a secondary option.
                                    </p>

                                    <div
                                        className="mt-7 pt-6 border-t"
                                        style={{
                                            borderColor:
                                                "rgba(255,255,255,0.10)",
                                        }}
                                    >
                                        <p
                                            className="text-xs font-semibold uppercase tracking-wider mb-3"
                                            style={{
                                                color: "var(--text-secondary)",
                                            }}
                                        >
                                            What we respond to
                                        </p>
                                        <ul className="space-y-2">
                                            {RESPOND_LIST.map((item, i) => (
                                                <li
                                                    key={item}
                                                    className="flex items-start gap-3 text-sm"
                                                    style={{
                                                        color: "var(--text-primary)",
                                                    }}
                                                >
                                                    <span
                                                        className="mt-2 inline-block w-1.5 h-1.5 rounded-full flex-shrink-0"
                                                        style={{
                                                            background:
                                                                [
                                                                    "#EC4899",
                                                                    "#8B5CF6",
                                                                    "#3B82F6",
                                                                    "#14B8A6",
                                                                ][i % 4],
                                                        }}
                                                        aria-hidden="true"
                                                    />
                                                    {item}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </GlassCard>
                            </FadeUp>
                        </div>

                        {/* Form card */}
                        <div className="lg:col-span-7">
                            <FadeUp delay={0.05}>
                                <GlassCard
                                    id="contact-form"
                                    className="p-6 sm:p-9"
                                    hoverLift={false}
                                    style={{ scrollMarginTop: "6rem" }}
                                >
                                    {form.success ? (
                                        <FormSuccessState
                                            heading="Message sent. Thank you."
                                            body={"We'll respond by email as soon as we can."}
                                            onReset={form.reset}
                                            testId="contact-success"
                                        />
                                    ) : (
                                        <form
                                            onSubmit={form.handleSubmit}
                                            noValidate
                                            data-testid="contact-form"
                                        >
                                            <h2 className="vfs-h2 mb-3">
                                                Send a message
                                            </h2>
                                            <p
                                                className="text-sm mb-7"
                                                style={{
                                                    color: "var(--text-secondary)",
                                                }}
                                            >
                                                We read every message
                                                ourselves.
                                            </p>
                                            <FormErrorBanner
                                                message={form.serverError}
                                            />
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                                <FormField
                                                    id="name"
                                                    name="name"
                                                    label="Name"
                                                    required
                                                    autoComplete="name"
                                                    maxLength={100}
                                                    value={form.values.name}
                                                    onChange={form.handleChange}
                                                    onBlur={form.handleBlur}
                                                    error={form.errors.name}
                                                    testId="contact-name"
                                                />
                                                <FormField
                                                    id="email"
                                                    name="email"
                                                    type="email"
                                                    label="Email"
                                                    required
                                                    autoComplete="email"
                                                    value={form.values.email}
                                                    onChange={form.handleChange}
                                                    onBlur={form.handleBlur}
                                                    error={form.errors.email}
                                                    testId="contact-email"
                                                    inputMode="email"
                                                />
                                                <FormField
                                                    id="phone"
                                                    name="phone"
                                                    type="tel"
                                                    label="Phone (optional)"
                                                    autoComplete="tel"
                                                    value={form.values.phone}
                                                    onChange={form.handleChange}
                                                    onBlur={form.handleBlur}
                                                    error={form.errors.phone}
                                                    testId="contact-phone"
                                                    inputMode="tel"
                                                />
                                                <FormSelect
                                                    id="reasonForContact"
                                                    name="reasonForContact"
                                                    label="Reason for contact"
                                                    required
                                                    options={REASONS}
                                                    value={
                                                        form.values
                                                            .reasonForContact
                                                    }
                                                    onChange={form.handleChange}
                                                    onBlur={form.handleBlur}
                                                    error={
                                                        form.errors
                                                            .reasonForContact
                                                    }
                                                    testId="contact-reason"
                                                />
                                            </div>
                                            <div className="mt-5">
                                                <FormTextarea
                                                    id="message"
                                                    name="message"
                                                    label="Message"
                                                    required
                                                    value={form.values.message}
                                                    onChange={form.handleChange}
                                                    onBlur={form.handleBlur}
                                                    error={form.errors.message}
                                                    testId="contact-message"
                                                />
                                            </div>

                                            <HoneypotField
                                                value={
                                                    form.values
                                                        .hp_company_website
                                                }
                                                onChange={form.handleChange}
                                            />

                                            <div className="mt-8">
                                                <FormSubmitButton
                                                    submitting={form.submitting}
                                                    testId="contact-submit"
                                                >
                                                    Send Message
                                                </FormSubmitButton>
                                            </div>
                                        </form>
                                    )}
                                </GlassCard>
                            </FadeUp>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
